import { useRouter, type Href } from 'expo-router';
import { Hospital } from 'lucide-react-native';

import { FeaturedBadge } from './featuredBadge';

import { primum_default_avatar } from '@/assets/images';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonGroup, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useInlineMarkup } from '@/hooks/useInlineMarkup';
import type { ActiveFeaturedExpertItem } from '@/services/getFeaturedExpertsV2';
import { segmentClient } from '@/util/analytics';
import { getAvatarUrl } from '@/util/getAvatarUrl';

type FeaturedExpertCardProps = {
  item: ActiveFeaturedExpertItem;
  onPress?: () => void;
};

/**
 * Card component for displaying a featured expert.
 *
 * Shows complete card with avatar, name, specialty, institution, cancer type badge, and description.
 * Used in the Featured tab on mobile and the desktop sidebar.
 *
 * Spacing design:
 * - gap-3 (12px): Standard spacing for avatar to text content
 * - mt-3 (12px): Top margin for secondary content section
 *
 * Testability:
 * - onPress prop allows test injection of navigation behavior
 *
 * Navigates to inquiry page on press with the expert's cancer type pre-selected.
 */
export function FeaturedExpertCard({ item, onPress }: FeaturedExpertCardProps) {
  const { navigate } = useRouter();

  const { expert, cancerType, description } = item;

  const formattedDescription = useInlineMarkup(description, { size: 'sm' });

  const trackAnalytics = (event: string) => {
    segmentClient.track(event, {
      expert_id: expert.id,
      expert_name: expert.name,
      source: 'featured_experts_section',
    });
  };

  const handleViewProfile = () => {
    trackAnalytics(ANALYTICS_EVENTS.EXPERT_SELECTED);

    navigate(`/profile/${expert.id}` as Href);
  };

  const handleMessage = () => {
    if (onPress) {
      onPress();

      return;
    }

    trackAnalytics(ANALYTICS_EVENTS.DIRECTORY_MESSAGE_SELECTED);

    const params = new URLSearchParams();

    if (cancerType?.id) {
      params.append('focusId', String(cancerType.id));
    }

    const queryString = params.toString();

    navigate(`/directory/inquiry/${expert.id}${queryString ? `?${queryString}` : ''}` as Href);
  };

  const card = (
    <Card variant="outline" className="p-4 md:hover:shadow-hard-1">
      <HStack className="items-center gap-3">
        <Avatar size="md">
          <AvatarImage source={getAvatarUrl(expert.avatar, true) || primum_default_avatar} />
        </Avatar>
        <VStack className="flex-1 justify-center" space="xs">
          <Text size="md" className="font-medium text-typography-900">
            {expert.name ?? ''}
          </Text>
          <HStack className="flex-wrap items-center gap-2">
            <FeaturedBadge label={cancerType.label} />
            {expert.specialty ? (
              <Badge action="info" className="gap-2 px-2" size="lg">
                <BadgeText className="text-xs text-info-800">{expert.specialty}</BadgeText>
              </Badge>
            ) : null}
          </HStack>
        </VStack>
      </HStack>

      <VStack className="mt-3 gap-2">
        {expert.institution ? (
          <HStack className="items-start gap-2">
            <Icon as={Hospital} size="sm" className="shrink-0 text-typography-500" />
            <Text size="sm" className="flex-1 text-typography-600">
              {expert.institution}
            </Text>
          </HStack>
        ) : null}

        {description ? (
          <Text size="sm" className="text-typography-700">
            {formattedDescription}
          </Text>
        ) : null}
      </VStack>

      <ButtonGroup className="mt-4 flex-row justify-center gap-4 md:hidden">
        <Button size="xs" onPress={handleViewProfile} variant="outline" className="flex-1">
          <ButtonText>View Profile</ButtonText>
        </Button>
        <Button size="xs" onPress={handleMessage} className="flex-1">
          <ButtonText>Message</ButtonText>
        </Button>
      </ButtonGroup>
    </Card>
  );

  return (
    <>
      <Pressable onPress={handleMessage} className="hidden md:flex" testID="featured-expert-card">
        {card}
      </Pressable>
      <VStack className="md:hidden" testID="featured-expert-card">
        {card}
      </VStack>
    </>
  );
}
